import { apiClient } from '@/api/client'
import { endpoints } from '@/api/endpoints'
import {
  BillVm,
  BillListResponse,
  BillListItem,
  BillVendor,
  BillProject,
  BillItem,
  BillTaxRate,
  CostLocation,
  ProfitCenter,
  Department,
  BillAccount,
  BillLine,
  ApiResponse,
} from '@/api/types'

/**
 * Purchase Bills service
 */
export class BillsService {
  /**
   * Get paginated list of bills for a branch
   */
  async getBills(
    branchId: number,
    pageNumber: number,
    pageSize: number
  ): Promise<BillListResponse> {
    const url = endpoints.bills.getAll(branchId, pageNumber, pageSize)
    const response = await apiClient.get<ApiResponse<BillListResponse> | BillListResponse | any>(url)
    const payload = response.data

    if (payload && typeof payload === 'object') {
      if ('success' in payload && 'data' in payload) {
        return payload.data as BillListResponse
      }

      if ('data' in payload && Array.isArray(payload.data)) {
        return {
          totalRecords: payload.totalRecords ?? payload.data.length,
          pageNumber: payload.pageNumber ?? pageNumber,
          pageSize: payload.pageSize ?? pageSize,
          data: payload.data as BillListItem[],
        }
      }
    }

    if (Array.isArray(payload)) {
      return {
        totalRecords: payload.length,
        pageNumber,
        pageSize,
        data: payload as BillListItem[],
      }
    }

    return {
      totalRecords: payload?.totalRecords ?? 0,
      pageNumber: payload?.pageNumber ?? pageNumber,
      pageSize: payload?.pageSize ?? pageSize,
      data: payload?.data ?? [],
    }
  }

  /**
   * Get bill by ID with lines
   * Calls two separate endpoints:
   * 1. GetBillById - for bill header data (only needs billId)
   * 2. GetBillLinesByBillId - for bill lines (needs branchId and billId)
   */
  async getBillById(branchId: number, billId: number): Promise<BillVm> {
    try {
      // Call both endpoints in parallel
      const [billHeaderResponse, billLinesResponse] = await Promise.all([
        apiClient.get<any>(endpoints.bills.getById(billId)),
        apiClient.get<any>(endpoints.bills.getBillLines(branchId, billId))
      ])
      
      console.log('🔵 [BillsService.getBillById] Calling GetBillById:', endpoints.bills.getById(billId))
      console.log('🔵 [BillsService.getBillById] Calling GetBillLinesByBillId:', endpoints.bills.getBillLines(branchId, billId))
      
      console.log('🔵 [BillsService.getBillById] Bill Header Response:', billHeaderResponse.data)
      console.log('🔵 [BillsService.getBillById] Bill Lines Response:', billLinesResponse.data)
      
      // Extract bill header data
      let billData: any = null
      const headerResponseData = billHeaderResponse.data
      
      // Helper function to extract data from nested structures
      const extractData = (data: any): any => {
        if (!data || typeof data !== 'object') return data
        
        if ('success' in data && 'data' in data) {
          return extractData(data.data)
        }
        
        if ('data' in data) {
          return extractData(data.data)
        }
        
        return data
      }
      
      const extractedHeaderData = extractData(headerResponseData)
      console.log('🔵 [BillsService.getBillById] Extracted Header Data:', JSON.stringify(extractedHeaderData, null, 2))
      
      // Handle bill header response
      if (extractedHeaderData && typeof extractedHeaderData === 'object' && !Array.isArray(extractedHeaderData)) {
        billData = extractedHeaderData
      } else if (Array.isArray(extractedHeaderData) && extractedHeaderData.length > 0) {
        billData = extractedHeaderData[0]
      } else {
        console.warn('⚠️ [BillsService.getBillById] Invalid bill header data, using empty object')
        billData = {}
      }
      
      // Extract bill lines data
      let billLines: any[] = []
      const linesResponseData = billLinesResponse.data
      const extractedLinesData = extractData(linesResponseData)
      console.log('🔵 [BillsService.getBillById] Extracted Lines Data:', JSON.stringify(extractedLinesData, null, 2))
      
      // Handle bill lines response - it's an array
      if (Array.isArray(extractedLinesData)) {
        billLines = extractedLinesData
      } else if (extractedLinesData && typeof extractedLinesData === 'object') {
        // Check if lines are nested in a property
        if ('billLines' in extractedLinesData && Array.isArray(extractedLinesData.billLines)) {
          billLines = extractedLinesData.billLines
        } else if ('billsLines' in extractedLinesData && Array.isArray(extractedLinesData.billsLines)) {
          billLines = extractedLinesData.billsLines
        }
      }
      
      console.log('🔵 [BillsService.getBillById] Final billData keys:', Object.keys(billData))
      console.log('🔵 [BillsService.getBillById] Final billLines count:', billLines.length)
      if (billLines.length > 0) {
        console.log('🔵 [BillsService.getBillById] First bill line:', billLines[0])
      }
      
      return this.mapBillData(billData, branchId, billLines)
    } catch (error: any) {
      console.error('❌ [BillsService.getBillById] Error:', error)
      console.error('❌ [BillsService.getBillById] Error Response:', error.response)
      console.error('❌ [BillsService.getBillById] Error Data:', error.response?.data)
      throw error
    }
  }

  /**
   * Map API response to BillVm interface
   * API Response Structure:
   * {
   *   "billId": 5,
   *   "billDate": "2025-12-06T00:00:00",
   *   "vendorId": 12,
   *   "dueDate": "2025-12-09T00:00:00",
   *   "subTotal": 100,
   *   "totalTax": 15,
   *   "total": 115,
   *   "note": "test",
   *   "vendorBillDate": "2025-12-10T19:00:00Z",
   *   "projectId": 3,
   *   "destinationAccount": 1157,
   *   ...
   * }
   */
  private mapBillData(billData: any, branchId: number, billLines?: any[]): BillVm {
    // Ensure billData is an object
    if (!billData || typeof billData !== 'object') {
      console.warn('⚠️ [BillsService.mapBillData] billData is invalid, using empty object')
      billData = {}
    }
    
    // Map bill lines if provided, otherwise use from billData
    const lines = billLines || billData.billLines || billData.billsLines || []
    
    // Map each bill line to ensure proper structure
    const mappedLines: BillLine[] = []
    if (Array.isArray(lines)) {
      for (const line of lines) {
        if (!line || typeof line !== 'object') {
          console.warn(`⚠️ [BillsService.mapBillData] Invalid line:`, line)
          continue
        }
        
        mappedLines.push({
          lineId: line.lineId || 0,
          billId: line.billId || billData.billId || 0,
          itemId: line.itemId || 0,
          description: line.description || '',
          qty: Number(line.qty) || 0,
          unitPrice: Number(line.unitPrice) || 0,
          itemPrice: Number(line.itemPrice) || 0,
          discountPercent: Number(line.discountPercent) || 0,
          discountAmount: Number(line.discountAmount) || 0,
          taxableAmount: Number(line.taxableAmount) || 0,
          taxRateId: line.taxRateId || 0,
          taxRate: Number(line.taxRate) || 0,
          taxAmount: Number(line.taxAmount) || 0,
          lineAmount: Number(line.lineAmount) || 0,
          debitAccount: line.debitAccount || 0,
          departmentId: line.departmentId || 0,
          costProfitCenterId: line.costProfitCenterId || 0,
          costLocationId: line.costLocationId || 0,
          taxPercent: Number(line.taxRate) || 0, // taxRate and taxPercent are same
          isDeleted: line.isDeleted || 'false',
          transactionTypeIdD: line.transactionTypeIdD,
        })
      }
    }
    
    console.log('🔵 [BillsService.mapBillData] Mapped bill lines:', mappedLines.length, 'items')
    if (mappedLines.length > 0) {
      console.log('🔵 [BillsService.mapBillData] Sample line:', mappedLines[0])
    }
    
    // Format date from ISO string to YYYY-MM-DD format for input fields
    const formatDateForInput = (dateString?: string): string => {
      if (!dateString) return ''
      try {
        // If already in YYYY-MM-DD format, return as is
        if (dateString.match(/^\d{4}-\d{2}-\d{2}$/)) {
          return dateString
        }
        // If contains T (ISO format), extract date part
        if (dateString.includes('T')) {
          return dateString.split('T')[0]
        }
        return dateString
      } catch {
        return dateString || ''
      }
    }
    
    // Get bill ID from various possible fields
    const billId = billData.billId || billData.id || 0
    
    // Map vendorBillDate - handle different formats
    let vendorBillDate = ''
    if (billData.vendorBillDate) {
      vendorBillDate = formatDateForInput(billData.vendorBillDate)
    } else if (billData.billDate) {
      vendorBillDate = formatDateForInput(billData.billDate)
    }
    
    return {
      billId: billId,
      billDate: formatDateForInput(billData.billDate) || new Date().toISOString().split('T')[0],
      vendorId: billData.vendorId || 0,
      projectId: billData.projectId || 0,
      dueDate: formatDateForInput(billData.dueDate) || '',
      subTotal: Number(billData.subTotal) || 0,
      totalTax: Number(billData.totalTax) || 0,
      total: Number(billData.total) || 0,
      note: billData.note || '',
      branchId: billData.branchId || branchId,
      vendorBillDate: vendorBillDate || new Date().toISOString().split('T')[0],
      ipAddress: billData.ipAddress || '',
      billLines: mappedLines,
      retentionPercent: billData.retentionPercent,
      totalRetentionAmount: billData.totalRetentionAmount,
      transactionModuleId: billData.transactionModuleId,
      transactionTypeIdD: billData.transactionTypeIdD,
    }
  }

  /**
   * Get all vendors for a branch
   */
  async getAllVendors(branchId: number): Promise<BillVendor[]> {
    const url = endpoints.bills.getAllVendors(branchId)
    const response = await apiClient.get<ApiResponse<BillVendor[]> | BillVendor[]>(url)
    
    if (Array.isArray(response.data)) {
      return response.data
    }
    
    if (response.data && 'data' in response.data && Array.isArray(response.data.data)) {
      return response.data.data
    }
    
    return []
  }

  /**
   * Get all projects for a branch
   */
  async getProjects(branchId: number): Promise<BillProject[]> {
    const url = endpoints.bills.getProject(branchId)
    const response = await apiClient.get<ApiResponse<BillProject[]> | BillProject[]>(url)
    
    if (Array.isArray(response.data)) {
      return response.data
    }
    
    if (response.data && 'data' in response.data && Array.isArray(response.data.data)) {
      return response.data.data
    }
    
    return []
  }

  /**
   * Get all items for a branch
   */
  async getItems(branchId: number): Promise<BillItem[]> {
    const url = endpoints.bills.getItemList(branchId)
    const response = await apiClient.get<ApiResponse<BillItem[]> | BillItem[]>(url)
    
    if (Array.isArray(response.data)) {
      return response.data
    }
    
    if (response.data && 'data' in response.data && Array.isArray(response.data.data)) {
      return response.data.data
    }
    
    return []
  }

  /**
   * Get all tax rates for a branch
   */
  async getTaxRates(branchId: number): Promise<BillTaxRate[]> {
    const url = endpoints.bills.getTaxRates(branchId)
    const response = await apiClient.get<ApiResponse<BillTaxRate[]> | BillTaxRate[]>(url)
    
    if (Array.isArray(response.data)) {
      return response.data
    }
    
    if (response.data && 'data' in response.data && Array.isArray(response.data.data)) {
      return response.data.data
    }
    
    return []
  }

  /**
   * Get all cost locations
   */
  async getCostLocations(): Promise<CostLocation[]> {
    const url = endpoints.bills.getCostLocation
    const response = await apiClient.get<ApiResponse<CostLocation[]> | CostLocation[]>(url)
    
    if (Array.isArray(response.data)) {
      return response.data
    }
    
    if (response.data && 'data' in response.data && Array.isArray(response.data.data)) {
      return response.data.data
    }
    
    return []
  }

  /**
   * Get all profit centers
   */
  async getProfitCenters(): Promise<ProfitCenter[]> {
    const url = endpoints.bills.getProfitCenter
    const response = await apiClient.get<ApiResponse<ProfitCenter[]> | ProfitCenter[]>(url)
    
    if (Array.isArray(response.data)) {
      return response.data
    }
    
    if (response.data && 'data' in response.data && Array.isArray(response.data.data)) {
      return response.data.data
    }
    
    return []
  }

  /**
   * Get all departments
   */
  async getDepartments(): Promise<Department[]> {
    const url = endpoints.bills.getDepartments
    const response = await apiClient.get<ApiResponse<Department[]> | Department[]>(url)
    
    if (Array.isArray(response.data)) {
      return response.data
    }
    
    if (response.data && 'data' in response.data && Array.isArray(response.data.data)) {
      return response.data.data
    }
    
    return []
  }

  /**
   * Get all accounts for a branch
   */
  async getAllAccounts(branchId: number): Promise<BillAccount[]> {
    try {
      const url = endpoints.bills.getAllAccounts(branchId)
      console.log('🔵 [BillsService.getAllAccounts] Calling:', url, 'branchId:', branchId)
      const response = await apiClient.get<ApiResponse<BillAccount[]> | BillAccount[] | any>(url)
      console.log('🔵 [BillsService.getAllAccounts] Full Response:', response)
      console.log('🔵 [BillsService.getAllAccounts] Response Data:', response.data)
      
      const payload = response.data
      
      // Helper to map account data
      const mapAccount = (acc: any): BillAccount => {
        return {
          accountId: acc.accountId || acc.id || 0,
          id: acc.id || acc.accountId || 0,
          accountCode: acc.accountCode || acc.code || '',
          accountName: acc.accountName || acc.accountNameEn || acc.nameEn || acc.name || '',
          accountNameEn: acc.accountNameEn || acc.nameEn || acc.name || '',
          accountNameAr: acc.accountNameAr || acc.nameAr || '',
        }
      }
      
      // Handle different response formats
      if (Array.isArray(payload)) {
        console.log('🔵 [BillsService.getAllAccounts] Returning array:', payload.length, 'items')
        return payload.map(mapAccount)
      }
      
      if (payload && typeof payload === 'object') {
        // Check if wrapped in success/data structure
        if ('success' in payload && 'data' in payload) {
          const data = payload.data
          if (Array.isArray(data)) {
            console.log('🔵 [BillsService.getAllAccounts] Returning from success.data:', data.length, 'items')
            return data.map(mapAccount)
          }
        }
        
        // Check if data is in 'data' property
        if ('data' in payload && Array.isArray(payload.data)) {
          console.log('🔵 [BillsService.getAllAccounts] Returning from data property:', payload.data.length, 'items')
          return payload.data.map(mapAccount)
        }
        
        // Check if it's a single account object
        if ('id' in payload || 'accountId' in payload) {
          console.log('🔵 [BillsService.getAllAccounts] Returning single account')
          return [mapAccount(payload)]
        }
      }
      
      console.warn('⚠️ [BillsService.getAllAccounts] Unexpected response format:', payload)
      return []
    } catch (error: any) {
      console.error('❌ [BillsService.getAllAccounts] Error:', error)
      console.error('❌ [BillsService.getAllAccounts] Error Response:', error.response)
      console.error('❌ [BillsService.getAllAccounts] Error Data:', error.response?.data)
      throw error
    }
  }

  /**
   * Save new bill
   */
  async saveBill(bill: BillVm): Promise<any> {
    const url = endpoints.bills.insert
    const response = await apiClient.post<ApiResponse<any> | any>(url, bill)
    return response.data
  }

  /**
   * Update existing bill
   */
  async updateBill(bill: BillVm): Promise<any> {
    const url = endpoints.bills.update
    const response = await apiClient.put<ApiResponse<any> | any>(url, bill)
    return response.data
  }
}

// Export singleton instance
export const billsService = new BillsService()

